import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { BookOpen, ChevronRight, Target, Brain, Home } from "lucide-react";
import PromoSidebar from "../components/PromoSidebar";

const API = window.location.origin;

const EXAM_HIERARCHY = { tgt: 1, pgt: 1, ctet: 1, kvs: 1, nvs: 1, dsssb: 1 };
const EXAM_ICONS = { tgt: "🎓", pgt: "📚", ctet: "✏️", kvs: "🏫", nvs: "🎯", dsssb: "📋" };
const EXAM_COLORS = {
  tgt: "from-indigo-600 to-blue-700", pgt: "from-purple-600 to-violet-700",
  ctet: "from-emerald-600 to-teal-700", kvs: "from-amber-600 to-orange-700",
  nvs: "from-rose-600 to-pink-700", dsssb: "from-cyan-600 to-sky-700"
};

export default function QuestionBankIndex() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/question-bank/public/index`)
      .then(r => setExams(r.data.exams || []))
      .catch(() => setExams([]))
      .finally(() => setLoading(false));
  }, []);

  const totalQ = exams.reduce((s, e) => s + e.total_questions, 0);

  const schema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: "Descriptive Test - TGT PGT Question Bank",
    url: `${window.location.origin}/question-bank`,
    description: `Free ${totalQ}+ MCQ questions for TGT/PGT/CTET/KVS/NVS/DSSSB exam preparation`
  };

  return (
    <>
      <Helmet>
        <title>TGT PGT MCQ Question Bank [Free] - Descriptive Test | India's Best Teaching Exam Prep</title>
        <meta name="description" content={`Practice ${totalQ}+ MCQ questions for TGT, PGT, CTET, KVS, NVS, DSSSB exam preparation. Topic-wise questions with detailed answers. Powered by Descriptive Test.`} />
        <meta name="keywords" content="TGT MCQ questions, PGT questions, CTET practice questions, KVS question bank, NVS MCQ, DSSSB questions, teaching exam preparation, Descriptive Test" />
        <meta property="og:title" content="TGT PGT MCQ Question Bank - Descriptive Test" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://descriptivetest.com/question-bank" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0d14]">
        {/* Hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#0a0d14] via-[#0f1520] to-[#0a0d14] border-b border-white/5">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900/20 via-transparent to-transparent" />
          <div className="relative max-w-6xl mx-auto px-4 py-16 text-center">
            <div className="flex justify-center mb-5">
              <Link to="/dashboard" data-testid="home-btn" className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 rounded-lg transition-colors text-slate-300 hover:text-white font-medium text-sm">
                <Home className="w-4 h-4" /> Back to Dashboard
              </Link>
            </div>
            <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse" />
              <span className="text-indigo-300 text-sm font-medium">India's Best Teaching Exam Prep</span>
            </div>
            <h1 className="text-4xl sm:text-6xl font-black text-white mb-4 tracking-tight">
              MCQ Question Bank
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">
              Practice <span className="text-indigo-400 font-bold">{totalQ.toLocaleString()}+</span> questions for 
              TGT, PGT, CTET, KVS, NVS & DSSSB exams. AI-powered explanations. Free forever.
            </p>
            <p className="text-slate-500 text-sm">
              Powered by <span className="text-indigo-400 font-semibold">Descriptive Test</span>
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mt-10">
              {[
                { icon: <BookOpen className="w-4 h-4" />, val: `${totalQ.toLocaleString()}+`, label: "Questions" },
                { icon: <Target className="w-4 h-4" />, val: `${Object.keys(EXAM_HIERARCHY || {}).length || exams.length}`, label: "Exams" },
                { icon: <Brain className="w-4 h-4" />, val: "AI", label: "Explanations" }
              ].map((s, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3">
                  <div className="flex items-center justify-center gap-1.5 text-indigo-400 mb-1">{s.icon}</div>
                  <div className="text-xl font-bold text-white">{s.val}</div>
                  <div className="text-xs text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Exam Cards + Sidebar */}
        <div className="max-w-7xl mx-auto px-4 py-12 flex gap-8 items-start">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-white mb-2">Choose Your Exam</h2>
            <p className="text-slate-400 text-sm mb-8">Select an exam to browse subject-wise and topic-wise questions</p>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => <div key={i} className="h-36 bg-white/5 rounded-2xl animate-pulse" />)}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {exams.map(exam => {
                  const color = EXAM_COLORS[exam.exam_type] || EXAM_COLORS.tgt;
                  const icon = EXAM_ICONS[exam.exam_type] || "📝";
                  return (
                    <Link
                      key={exam.exam_type}
                      to={`/question-bank/${exam.exam_type}`}
                      data-testid={`exam-card-${exam.exam_type}`}
                      className="group relative bg-white/5 hover:bg-white/8 border border-white/10 hover:border-white/20 rounded-2xl p-6 transition-all duration-200 overflow-hidden"
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-5 transition-opacity`} />
                      <div className="relative">
                        <div className="text-3xl mb-3">{icon}</div>
                        <h3 className="text-lg font-bold text-white mb-1 group-hover:text-indigo-300 transition-colors">
                          {exam.exam_type.toUpperCase()}
                        </h3>
                        <p className="text-slate-400 text-xs mb-4 leading-relaxed">{exam.label}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-3 text-xs text-slate-500">
                            <span className="text-emerald-400 font-semibold">{exam.total_questions.toLocaleString()} Q</span>
                            <span>{exam.subject_count} Subjects</span>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* How it works */}
            <div className="mt-12 pt-10 border-t border-white/5">
              <h2 className="text-xl font-bold text-white mb-6 text-center">How to Prepare</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {[
                  { step: "01", title: "Choose Exam & Topic", desc: "Select your target exam (TGT/PGT/CTET etc.) and browse subject-wise topics" },
                  { step: "02", title: "Practice Questions", desc: "Attempt MCQs in quiz mode or practice mode with instant feedback" },
                  { step: "03", title: "Review & Improve", desc: "Read AI-powered explanations and track your performance" }
                ].map(s => (
                  <div key={s.step} className="bg-white/5 border border-white/10 rounded-xl p-5">
                    <div className="text-2xl font-black text-indigo-500/40 mb-2">{s.step}</div>
                    <h3 className="text-white font-semibold mb-2">{s.title}</h3>
                    <p className="text-slate-400 text-sm">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Promo Sidebar */}
          <PromoSidebar />
        </div>

        <div className="border-t border-white/5 py-8 text-center">
          <p className="text-slate-500 text-sm">
            © 2026 <Link to="/" className="text-indigo-400 hover:text-indigo-300">Descriptive Test</Link> — India's Best TGT/PGT Preparation Platform
          </p>
        </div>
      </div>
    </>
  );
}
